import { useCallback, useEffect, useState } from "react";
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectAccountData } from "../redux/main/accountSlice";

export const JobSelect = ({ selectedOption, setSelectedOption, isMulti = false, placeholder = "Seleziona commessa..." }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const accountId = accountData?._id;

    // Funzione per eseguire la chiamata API con parametri di ricerca avanzati
    const fetchJobs = async (searchText) => {
        if (!accountId) return;
        
        setIsLoading(true);
        try {
            // Uso l'endpoint getForSelect2 che è molto più veloce
            let url = `${import.meta.env.VITE_API_BASE_URL}/jobs/getForSelect2?accountId=${accountId}&pagination=50`;
            
            // Aggiungi il parametro di ricerca solo se ha una lunghezza sufficiente
            if (searchText && searchText.length >= 2) {
                url += `&searchTerm=${encodeURIComponent(searchText)}`;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore API: ${response.status}`);
            }
            
            const data = await response.json();
            
            // L'endpoint getForSelect2 restituisce { results: [...], ... }
            const items = data.results || [];
            
            // Trasforma i dati nel formato richiesto da react-select
            // L'endpoint getForSelect2 restituisce già la label formattata nel campo 'text'
            const formattedOptions = items.map(job => ({
                value: job.id || job._id,
                label: job.text || job.title || `${job.code || ''} - ${job.customerLabel || 'N/D'}`,
                subLabel: job.subLabel || '',
                systemLabel: job.systemLabel || '',
                systemAddressLabel: job.systemAddressLabel || '',
                data: job
            }));
            
            setOptions(formattedOptions);
        } catch (error) {
            console.error('Errore nel caricamento delle commesse:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce per evitare troppe chiamate API durante la digitazione
    const debouncedFetchJobs = useCallback(
        debounce(fetchJobs, 300),
        [accountId, token]
    );

    // Carica le opzioni iniziali
    useEffect(() => {
        fetchJobs('');
    }, [accountId, token]);

    // Gestisce la modifica dell'input di ricerca
    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        debouncedFetchJobs(newValue);
    };

    // Stile personalizzato per react-select
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
            },
            borderRadius: '0.375rem',
            backgroundColor: state.isDisabled ? '#f7fafc' : 'white',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#4299e1' 
                : state.isFocused 
                    ? '#ebf8ff' 
                    : base.backgroundColor,
            color: state.isSelected ? 'white' : '#4a5568',
            ':active': {
                backgroundColor: state.isSelected ? '#3182ce' : '#e6f7ff'
            },
            padding: '8px 12px'
        }),
        singleValue: (base) => ({
            ...base,
            color: '#2d3748',
            fontWeight: 500,
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#e9d8fd',
            borderRadius: '0.25rem',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#553c9a',
            fontWeight: 500,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#553c9a',
            ':hover': {
                backgroundColor: '#d6bcfa',
                color: '#44337a'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }),
        noOptionsMessage: (base) => ({
            ...base,
            fontSize: '0.875rem',
            color: '#718096'
        })
    };

    // Formatta la visualizzazione delle opzioni con impianto e indirizzo in corsivo
    const formatOptionLabel = (option, { context }) => {
        // Nel menu dropdown mostra label principale + sublabel in corsivo
        if (context === 'menu') {
            return (
                <div>
                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                    {option.subLabel && (
                        <div style={{ 
                            fontSize: '0.75rem', 
                            fontStyle: 'italic', 
                            color: '#718096',
                            marginTop: '2px'
                        }}>
                            {option.subLabel}
                        </div>
                    )}
                </div>
            );
        }
        // Nel valore selezionato mostra solo la label principale
        return option.label;
    };

    return (
        <Select
            isMulti={isMulti}
            options={options}
            value={selectedOption}
            onChange={setSelectedOption}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            isLoading={isLoading}
            placeholder={placeholder}
            noOptionsMessage={() => "Nessuna commessa trovata"}
            loadingMessage={() => "Caricamento commesse..."}
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            isClearable
            classNamePrefix="job-select"
        />
    );
};

export default JobSelect;
